import React from "react";
import { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import axios from "axios";
import useCurrentUser from "../hooks/useCurrentUser";
import Topbar from "../components/Topbar";
import ManageClassForm from "../components/Forms/ManageClassForm";
import ManageStudentForm from "../components/Forms/ManageStudentForm";
import ManageTeacherForm from "../components/Forms/ManageTeacherForm";
import ManageAdminForm from "../components/Forms/ManageAdminForm";
import EntityTable from "../components/EntityTable";
import ManageEntityPopup from "../components/ManageEntityPopup";
import "../css/AdminPage.css";
import { EntityManagementContext } from "../context/EntityManagementContext";

// On this page, admin and teachers can manage entities 
// (students, teachers, admin, classes) within their permissions.
function Admin() {
    let user = null;
    user = useCurrentUser();

    const [loading, setLoading] = useState(true);
    const [students, setStudents] = useState([]); // List of all students
    const [teachers, setTeachers] = useState([]); // List of all teachers
    const [admins, setAdmins] = useState([]); // List of all admins
    const [classes, setClasses] = useState([]); // List of all classes
    const [currentlyManaging, setCurrentlyManaging] = useState(false); // Boolean representing whether to show entity manage form popup for responsiveness
    const [mode, setMode] = useState("Create"); // Current management mode; "Create" or "Edit" entities
    const [editingID, setEditingID] = useState(-1); // Current user being edited
    // Current entity type
    const [entityType, setEntityType] = useState(sessionStorage.getItem("adminTab")
        ? sessionStorage.getItem("adminTab")
        : "Student");

    // Column headers for each user table
    const adminHeaders = ["Admin", "Username", "Actions"];
    const teacherHeaders = ["Teacher", "Username", "Classes", "Actions"];
    const studentHeaders = ["Student", "Username", "Age", "Actions"];
    const classHeaders = ["Class", "Teacher", "Grade", "Actions"];

    // Switch tabs in entity table
    const switchTabs = (selectedKey) => {
        setEntityType(selectedKey);
        setMode("Create"); // Ensure mode is create, not edit
        sessionStorage.setItem("adminTab", selectedKey);
        setEditingID(-1); // Reset ID
    }

    // Toggle entity manage form popup (create/edit in responsive mode)
    const openManagerPopup = () => {
        setCurrentlyManaging(!currentlyManaging);
    }

    // Close entity manage form popup
    const exitPopup = () => {
        setCurrentlyManaging(false);
    }

    useEffect(() => {
        if (mode == "Edit") {
            setCurrentlyManaging(true);
        }
    }, [mode]);

    // Students should not be able to access this page
    // Redirect to Explore page
    useEffect(() => {
        if (user && !user.is_staff) {
            window.location.href = '/explore';
        }
    }, [user])

    // Get lists of all entity types on each re-render
    useEffect(() => {
        // Get all students in the system
        axios
            .get("/api/students/")
            .then((response) => {
                setStudents(response.data);
                setLoading(false);
            })
            .catch((error) => {
                // TODO: Add error handling
            });
        // Get all teachers in the system
        axios
            .get("/api/teachers/")
            .then((response) => {
                setTeachers(response.data);
            })
            .catch((error) => {
                // TODO: Add error handling
            });
        // Get all admins in the system
        axios
            .get("/api/admins/")
            .then((response) => {
                setAdmins(response.data);
            })
            .catch((error) => {
                // TODO: Add error handling
            });
        // Get all classes in the system
        axios
            .get("/api/classes/")
            .then((response) => {
                setClasses(response.data);
            })
            .catch((error) => {
                // TODO: Add error handling
            });
    }, []);

    // Update entity table to reflect creation or update of an entity.
    //      newEntity: The newly created or updated entity object
    //      editedPassword: If the user has just edited their own password
    function updateEntities(newEntity, editedPassword = false) {
        // If the user has just edited their own password, they
        // have been logged out
        if (editedPassword) {
            window.location.reload();
            return;
        }

        // What field does the entity use as an identifier?
        let identifier;
        if (entityType == "Class") {
            identifier = "classID";
        } else {
            identifier = "id";
        }

        // What state does this entity use?
        let entities;
        let setEntities;
        if (entityType == "Student") {
            entities = students;
            setEntities = setStudents;
        } else if (entityType == "Teacher") {
            entities = teachers;
            setEntities = setTeachers;
        } else if (entityType == "Admin") {
            entities = admins;
            setEntities = setAdmins;
        } else if (entityType == "Class") {
            entities = classes;
            setEntities = setClasses;
        }

        // Replace old version of entity with updated one
        if (mode == "Edit") {
            let mutableEntities = entities;
            let targetEntityIdx = mutableEntities.findIndex(entity => entity[identifier] == newEntity[identifier]);
            
            // If the edited entity was a class, ensure that the teacher
            // records are updated if the class' teacher has changed
            if (entityType == "Class") {
                let mutableTeachers = teachers;

                let oldTeacherID = mutableEntities[targetEntityIdx].teacher;
                let newTeacherID = newEntity.teacher;

                let oldTeacherIdx = mutableTeachers.findIndex(teacher => teacher.id == oldTeacherID);
                let newTeacherIdx = mutableTeachers.findIndex(teacher => teacher.id == newTeacherID);

                // Remove from old teacher's classes list
                mutableTeachers[oldTeacherIdx].classes = mutableTeachers[oldTeacherIdx].classes.filter(myClass => myClass != newEntity[identifier]);

                // Add to new teacher's classes list
                mutableTeachers[newTeacherIdx].classes = [...mutableTeachers[newTeacherIdx].classes, newEntity[identifier]];

                setTeachers(mutableTeachers);
            }
            
            mutableEntities[targetEntityIdx] = newEntity;
            setEntities(mutableEntities);
            setEditingID(-1); // Reset ID
        } 
        // Add entry to existing entity list
        else if (mode == "Create") {
            setEntities([...entities, newEntity]);

            if (entityType == "Class") {
                let mutableTeachers = teachers;
                
                let newTeacherID = newEntity.teacher;
                let newTeacherIdx = mutableTeachers.findIndex(teacher => teacher.id == newTeacherID);

                // Add to new teacher's classes list
                mutableTeachers[newTeacherIdx].classes = [...mutableTeachers[newTeacherIdx].classes, newEntity[identifier]];

                setTeachers(mutableTeachers);
            }
        }
    }

    const entityManagementContext = {
        editingID: editingID,
        mode: mode,
        exit: exitPopup,
        type: entityType,
        classes: classes,
        setClasses: setClasses,
        teachers: teachers,
        setTeachers: setTeachers,
        admins: admins,
        setAdmins: setAdmins,
        students: students,
        setStudents: setStudents,
        setEditingID: setEditingID,
        setMode: setMode,
        openPopup: openManagerPopup,
        updateEntities: updateEntities
    }

    return (
        <div id="admin-page">
            <EntityManagementContext.Provider value={entityManagementContext}>
                {/* When browser window is narrow, show entity form as popup */}
                {currentlyManaging ?
                    <ManageEntityPopup/>
                    : null}
                <Topbar isAdminPage={true} /> {/* Navbar */}
                {loading && (
                    <div id="loading-msg-area">
                        <span>Loading...<br />This may take a minute.</span>
                    </div>
                )}
                <div id="admin-layout"> {/* Entire page excluding navbar */}
                    <div id="admin-panels"> {/* Area containing entity table and manage form */}
                        <div id="main-admin"> {/* Area containing entity table */}
                            <Card id="table-area">
                                <Card.Header className="light-purple white-text">
                                    Manage Users & Classes
                                </Card.Header>
                                <Card.Body id="scrollable-table"> {/* Entity table, shows lists of every entity in the system, by type */}
                                    <Tabs defaultActiveKey={entityType} onSelect={switchTabs}>
                                        <Tab eventKey="Student" title="Students">
                                            <EntityTable
                                                type="Student"
                                                headers={studentHeaders}
                                                users={students}
                                                editFunc={null} />
                                        </Tab>
                                        {user && user.is_superuser ? (
                                            <Tab eventKey="Teacher" title="Teachers">
                                                <EntityTable
                                                    type="Teacher"
                                                    headers={teacherHeaders}
                                                    users={teachers}
                                                    editFunc={null} />
                                            </Tab>
                                        ) : null}
                                        {user && user.is_superuser ? (
                                            <Tab eventKey="Admin" title="Admin">
                                                <EntityTable
                                                    type="Admin"
                                                    headers={adminHeaders}
                                                    users={admins}
                                                    editFunc={null} />
                                            </Tab>
                                        ) : null}
                                        <Tab eventKey="Class" title="Classes">
                                            <EntityTable
                                                type="Class"
                                                user={user}
                                                headers={classHeaders}
                                                users={classes}
                                                editFunc={null} />
                                        </Tab>
                                    </Tabs>
                                </Card.Body>
                            </Card>
                            <Card id="new-user-panel"> {/* Manage Entity form */}
                                <Card.Header className="light-purple white-text">
                                    {mode} {entityType}
                                </Card.Header>
                                <Card.Body>
                                    {entityType == "Student" ?
                                        <ManageStudentForm/>
                                        : (entityType == "Teacher" ?
                                            <ManageTeacherForm/>
                                            : (entityType == "Admin" ?
                                                (<ManageAdminForm/>)
                                                : (entityType == "Class" ?
                                                    <ManageClassForm/>
                                                    : null)))}
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </div>
            </EntityManagementContext.Provider>
        </div>
    );
}

export default Admin;
